/**
 * Tables Management Page - Complete Edition
 * Monitor and manage active poker tables with real-time updates
 */

import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/adminService';

interface Table {
  id: string;
  name: string;
  status: string;
  currentPlayers: number;
  maxPlayers: number;
  anteAmount: number;
  minBuyIn?: number;
  maxBuyIn?: number;
  isPrivate: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface TableDetails extends Table {
  players?: Array<{
    id: string;
    username: string;
    chipCount: number;
    seatPosition: number;
    status: string;
  }>;
  currentHand?: {
    handNumber: number;
    pot: number;
    phase: string;
  };
  handHistory?: Array<{
    id: string;
    handNumber: number;
    pot: number;
    winnerId: string;
    endTime: string;
  }>;
}

type StatusFilter = 'all' | 'active' | 'empty' | 'waiting';
type SortField = 'name' | 'players' | 'stakes' | 'created';

const TablesPage: React.FC = () => {
  // State
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('created');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalTable, setDetailsModalTable] = useState<TableDetails | null>(null);
  const [kickPlayerModalTable, setKickPlayerModalTable] = useState<Table | null>(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    maxPlayers: 6,
    anteAmount: 5,
    minBuyIn: 1000,
    maxBuyIn: 10000,
    isPrivate: false,
    autoStartBots: false,
  });

  // Load tables on mount and when filters change
  useEffect(() => {
    loadTables();
  }, [page, statusFilter, sortField]);

  // Real-time updates every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadTables(true); // Silent refresh
    }, 5000);

    return () => clearInterval(interval);
  }, [page, statusFilter, sortField]);

  const loadTables = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params: any = { page, limit: 20 };

      // Apply filters
      if (statusFilter === 'active') {
        params.status = 'ACTIVE';
      } else if (statusFilter === 'waiting') {
        params.status = 'WAITING';
      } else if (statusFilter === 'empty') {
        params.currentPlayers = 0;
      }

      const data = await adminService.getTables(params);

      // Apply client-side sorting
      let sortedTables = [...data.data];
      switch (sortField) {
        case 'players':
          sortedTables.sort((a, b) => b.currentPlayers - a.currentPlayers);
          break;
        case 'stakes':
          sortedTables.sort((a, b) => b.anteAmount - a.anteAmount);
          break;
        case 'name':
          sortedTables.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'created':
          sortedTables.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          break;
      }

      setTables(sortedTables);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setLastUpdate(new Date());
    } catch (error: any) {
      console.error('Failed to load tables:', error);
      if (!silent) alert('Failed to load tables: ' + error.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // View table details
  const handleViewDetails = async (table: Table) => {
    try {
      const details = await adminService.getTableDetails(table.id);
      setDetailsModalTable(details);
    } catch (error: any) {
      console.error('Failed to load table details:', error);
      alert('Failed to load table details: ' + error.message);
    }
  };

  // Create table
  const handleCreateTable = async () => {
    try {
      // TODO: Add create table endpoint to adminService
      console.log('Create table:', createForm);
      alert('Table created! (Endpoint needs to be added to backend)');
      setCreateModalOpen(false);
      setCreateForm({
        name: '',
        maxPlayers: 6,
        anteAmount: 5,
        minBuyIn: 1000,
        maxBuyIn: 10000,
        isPrivate: false,
        autoStartBots: false,
      });
      loadTables();
    } catch (error: any) {
      console.error('Failed to create table:', error);
      alert('Failed to create table: ' + error.message);
    }
  };

  // Close table
  const handleCloseTable = async (tableId: string, tableName: string) => {
    if (!confirm(`Are you sure you want to force close "${tableName}"? Players will be disconnected.`)) {
      return;
    }

    try {
      await adminService.closeTable(tableId);
      loadTables();
      alert('Table closed successfully!');
    } catch (error: any) {
      console.error('Failed to close table:', error);
      alert('Failed to close table: ' + error.message);
    }
  };

  // Pause table
  const handlePauseTable = async (tableId: string) => {
    try {
      // TODO: Add pause table endpoint
      console.log('Pause table:', tableId);
      alert('Table paused! (Endpoint needs to be added)');
      loadTables();
    } catch (error: any) {
      console.error('Failed to pause table:', error);
      alert('Failed to pause table: ' + error.message);
    }
  };

  // Kick player
  const handleKickPlayer = async (tableId: string, playerId: string) => {
    try {
      await adminService.kickPlayerFromTable(tableId, { playerId });
      setKickPlayerModalTable(null);
      loadTables();
      alert('Player kicked successfully!');
    } catch (error: any) {
      console.error('Failed to kick player:', error);
      alert('Failed to kick player: ' + error.message);
    }
  };

  // View live (spectator mode)
  const handleViewLive = (tableId: string) => {
    alert(`Opening spectator mode for table ${tableId}...\n(Spectator feature coming soon!)`);
  };

  // Status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAUSED':
        return 'bg-orange-100 text-orange-800';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Format time ago
  const formatTimeAgo = (date: string): string => {
    const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Tables Management</h2>
            <p className="text-green-100 mt-1">Monitor and manage active poker tables</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-xs text-green-100">Last updated</div>
              <div className="text-sm font-medium">{lastUpdate.toLocaleTimeString()}</div>
              <div className="mt-1 flex items-center justify-end space-x-2">
                <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-200">Live</span>
              </div>
            </div>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="px-4 py-2 bg-white text-green-600 font-semibold rounded-lg hover:bg-green-50 transition-colors"
            >
              ➕ Create Table
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Tables</div>
          <div className="text-2xl font-bold text-gray-800">{total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Tables</div>
          <div className="text-2xl font-bold text-green-600">
            {tables.filter(t => t.status === 'ACTIVE').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Players</div>
          <div className="text-2xl font-bold text-blue-600">
            {tables.reduce((sum, t) => sum + t.currentPlayers, 0)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Empty Tables</div>
          <div className="text-2xl font-bold text-orange-600">
            {tables.filter(t => t.currentPlayers === 0).length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Filter:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as StatusFilter);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Show All</option>
                <option value="active">Active Only</option>
                <option value="waiting">Waiting Only</option>
                <option value="empty">Empty Only</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mr-2">Sort by:</label>
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="created">Created Time</option>
                <option value="players">Players</option>
                <option value="stakes">Stakes</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>

          <button
            onClick={() => loadTables()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tables List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading tables...</div>
        ) : tables.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl mb-4">🎲</div>
            <div className="text-gray-500">No tables found</div>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create First Table
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Table Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Players
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Stakes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tables.map((table) => (
                    <tr key={table.id} className="hover:bg-gray-50 cursor-pointer">
                      <td
                        className="px-6 py-4"
                        onClick={() => handleViewDetails(table)}
                      >
                        <div className="font-medium text-gray-900">{table.name}</div>
                        <div className="text-sm text-gray-500 flex items-center space-x-2">
                          <span>ID: {table.id.slice(0, 8)}</span>
                          {table.isPrivate && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                              🔒 Private
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div className={`text-lg font-bold ${
                            table.currentPlayers === 0 ? 'text-gray-400' :
                            table.currentPlayers === table.maxPlayers ? 'text-red-600' :
                            'text-green-600'
                          }`}>
                            {table.currentPlayers}
                          </div>
                          <div className="text-gray-500">/</div>
                          <div className="text-gray-700">{table.maxPlayers}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          Ante: {table.anteAmount}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(table.status)}`}>
                          {table.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatTimeAgo(table.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLive(table.id);
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                            title="View Live"
                          >
                            👁️
                          </button>
                          {table.currentPlayers > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setKickPlayerModalTable(table);
                              }}
                              className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-xs rounded transition-colors"
                              title="Kick Player"
                            >
                              🚫
                            </button>
                          )}
                          {table.status === 'ACTIVE' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePauseTable(table.id);
                              }}
                              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-colors"
                              title="Pause"
                            >
                              ⏸️
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTable(table.id, table.name);
                            }}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                            title="Close Table"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} tables
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <div className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Page {page} of {totalPages}
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Table Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Create New Table</h3>
              <button
                onClick={() => setCreateModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Name
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., High Stakes Table"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Players
                </label>
                <select
                  value={createForm.maxPlayers}
                  onChange={(e) => setCreateForm({ ...createForm, maxPlayers: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={6}>6 Players</option>
                  <option value={8}>8 Players</option>
                  <option value={9}>9 Players</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ante Amount
                </label>
                <input
                  type="number"
                  value={createForm.anteAmount}
                  onChange={(e) => setCreateForm({ ...createForm, anteAmount: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Buy-in
                  </label>
                  <input
                    type="number"
                    value={createForm.minBuyIn}
                    onChange={(e) => setCreateForm({ ...createForm, minBuyIn: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Buy-in
                  </label>
                  <input
                    type="number"
                    value={createForm.maxBuyIn}
                    onChange={(e) => setCreateForm({ ...createForm, maxBuyIn: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPrivate"
                  checked={createForm.isPrivate}
                  onChange={(e) => setCreateForm({ ...createForm, isPrivate: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="isPrivate" className="text-sm text-gray-700">
                  Private Table (password protected)
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="autoStartBots"
                  checked={createForm.autoStartBots}
                  onChange={(e) => setCreateForm({ ...createForm, autoStartBots: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="autoStartBots" className="text-sm text-gray-700">
                  Auto-start with AI bots
                </label>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setCreateModalOpen(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTable}
                disabled={!createForm.name}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Details Modal */}
      {detailsModalTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">
                {detailsModalTable.name}
              </h3>
              <button
                onClick={() => setDetailsModalTable(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Table Info */}
              <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getStatusColor(detailsModalTable.status)}`}>
                    {detailsModalTable.status}
                  </span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Players</div>
                  <div className="font-bold text-gray-800">
                    {detailsModalTable.currentPlayers}/{detailsModalTable.maxPlayers}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Ante</div>
                  <div className="font-bold text-gray-800">
                    {detailsModalTable.anteAmount}
                  </div>
                </div>
              </div>

              {/* Current Players */}
              <div>
                <h5 className="font-bold text-gray-800 mb-3">Current Players</h5>
                {detailsModalTable.players && detailsModalTable.players.length > 0 ? (
                  <div className="space-y-2">
                    {detailsModalTable.players.map((player) => (
                      <div
                        key={player.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                            {player.seatPosition + 1}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{player.username}</div>
                            <div className="text-sm text-gray-500">{player.status}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-green-600">
                            {player.chipCount.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">chips</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">No players at this table</div>
                )}
              </div>

              {/* Current Hand (if active) */}
              {detailsModalTable.currentHand && (
                <div>
                  <h5 className="font-bold text-gray-800 mb-3">Current Hand</h5>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-blue-600">Hand #</div>
                        <div className="font-bold text-blue-900">
                          {detailsModalTable.currentHand.handNumber}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-blue-600">Pot</div>
                        <div className="font-bold text-blue-900">
                          {detailsModalTable.currentHand.pot.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-blue-600">Phase</div>
                        <div className="font-bold text-blue-900">
                          {detailsModalTable.currentHand.phase}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hand History */}
              {detailsModalTable.handHistory && detailsModalTable.handHistory.length > 0 && (
                <div>
                  <h5 className="font-bold text-gray-800 mb-3">Hand History (Last 20)</h5>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {detailsModalTable.handHistory.map((hand) => (
                      <div
                        key={hand.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm"
                      >
                        <div>
                          <span className="font-medium">Hand #{hand.handNumber}</span>
                          <span className="text-gray-500 ml-2">
                            {new Date(hand.endTime).toLocaleString()}
                          </span>
                        </div>
                        <div className="font-bold text-green-600">
                          {hand.pot.toLocaleString()} chips
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => handleViewLive(detailsModalTable.id)}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  👁️ View Live (Spectator)
                </button>
                <button
                  onClick={() => {
                    handleCloseTable(detailsModalTable.id, detailsModalTable.name);
                    setDetailsModalTable(null);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Close Table
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kick Player Modal */}
      {kickPlayerModalTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-xl font-bold text-gray-800">
                Kick Player from {kickPlayerModalTable.name}
              </h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Select a player to kick from the table:
              </p>
              <div className="text-center text-gray-500 py-8">
                (Player list will be loaded here)
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Note: Use the table details view to see current players and kick them.
              </p>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setKickPlayerModalTable(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesPage;
